import { areDagRecordsEqual } from './dagPerformance';

const joinSearchValues = (values) => values
  .filter((value) => value !== null && value !== undefined)
  .map((value) => String(value).toLowerCase())
  .join(' ');

const MAX_SEARCH_DEPTH = 8;
const MAX_SEARCH_PARTS = 500;
const MAX_SEARCH_CHARACTERS = 20000;
const MAX_SEARCH_VISITS = 2000;
const MAX_STRUCTURED_SEARCH_CHARACTERS = 200000;
const SENSITIVE_SEARCH_KEYS = new Set([
  'accesstoken',
  'apikey',
  'authorization',
  'clientsecret',
  'cookie',
  'credential',
  'credentials',
  'documentpassword',
  'password',
  'passphrase',
  'privatekey',
  'refreshtoken',
  'secret',
  'sessiontoken',
]);

const normalizeSearchKey = (key) => String(key || '')
  .trim()
  .toLowerCase()
  .replace(/[^a-z0-9]/g, '');

const isSensitiveSearchKey = (key) => {
  const normalizedKey = normalizeSearchKey(key);
  if (!normalizedKey) return false;
  return SENSITIVE_SEARCH_KEYS.has(normalizedKey)
    || normalizedKey.endsWith('password')
    || normalizedKey.endsWith('secret')
    || normalizedKey.endsWith('token')
    || normalizedKey.endsWith('privatekey');
};

const appendSearchPart = (state, value) => {
  if (
    value === null
    || value === undefined
    || state.parts.length >= MAX_SEARCH_PARTS
    || state.characterCount >= MAX_SEARCH_CHARACTERS
  ) return;
  const text = String(value).trim().toLowerCase();
  if (!text || text === '***') return;
  const remainingCharacters = MAX_SEARCH_CHARACTERS - state.characterCount;
  const boundedText = text.slice(0, remainingCharacters);
  if (!boundedText) return;
  state.parts.push(boundedText);
  state.characterCount += boundedText.length + 1;
};

const appendSearchKey = (state, key) => {
  const text = String(key || '').trim();
  if (!text) return;
  appendSearchPart(state, text);
  const readableText = text
    .replace(/([a-z0-9])([A-Z])/g, '$1 $2')
    .replace(/[_-]+/g, ' ');
  if (readableText !== text) appendSearchPart(state, readableText);
};

const appendSearchValue = (state, value) => {
  const text = String(value || '').trim();
  if (!text) return;
  appendSearchPart(state, text);
  const readableText = text
    .replace(/([a-z0-9])([A-Z])/g, '$1 $2')
    .replace(/[_-]+/g, ' ');
  if (readableText !== text) appendSearchPart(state, readableText);
};

const parseStructuredSearchValue = (value) => {
  const text = String(value || '').trim();
  if (!text || !['{', '['].includes(text[0])) return null;
  // Do not parse unbounded payloads or index their raw credential fields.
  if (text.length > MAX_STRUCTURED_SEARCH_CHARACTERS) return {};
  try {
    const parsed = JSON.parse(text);
    return parsed && typeof parsed === 'object' ? parsed : null;
  } catch (_error) {
    return null;
  }
};

const collectSearchValues = (value, state, depth = 0) => {
  if (state.visitCount >= MAX_SEARCH_VISITS) return;
  state.visitCount += 1;
  if (
    value === null
    || value === undefined
    || depth > MAX_SEARCH_DEPTH
    || state.parts.length >= MAX_SEARCH_PARTS
    || state.characterCount >= MAX_SEARCH_CHARACTERS
  ) return;

  if (typeof value === 'string') {
    const structuredValue = parseStructuredSearchValue(value);
    if (structuredValue) {
      collectSearchValues(structuredValue, state, depth + 1);
    } else {
      appendSearchValue(state, value);
    }
    return;
  }
  if (['number', 'boolean', 'bigint'].includes(typeof value)) {
    appendSearchPart(state, value);
    return;
  }
  if (typeof value !== 'object') return;
  if (state.visited.has(value)) return;
  state.visited.add(value);

  if (Array.isArray(value)) {
    for (const item of value) {
      if (isSearchBudgetExhausted(state)) break;
      collectSearchValues(item, state, depth + 1);
    }
    return;
  }

  for (const key in value) {
    if (isSearchBudgetExhausted(state)) break;
    // Count keys even when excluded, so large credential maps stay bounded.
    state.visitCount += 1;
    if (!Object.prototype.hasOwnProperty.call(value, key) || isSensitiveSearchKey(key)) continue;
    appendSearchKey(state, key);
    collectSearchValues(value[key], state, depth + 1);
  }
};

const isSearchBudgetExhausted = (state) => (
  state.parts.length >= MAX_SEARCH_PARTS
  || state.characterCount >= MAX_SEARCH_CHARACTERS
  || state.visitCount >= MAX_SEARCH_VISITS
);

export const getDagSearchMetadataText = (...values) => {
  const state = {
    parts: [],
    characterCount: 0,
    visitCount: 0,
    visited: new WeakSet(),
  };
  values.forEach((value) => collectSearchValues(value, state));
  return state.parts.join(' ');
};

export const getDagNodeSearchText = (node) => {
  if (!node) return '';
  const item = node.item || {};
  const datasource = item.datasource || {};
  return joinSearchValues([
    node.key,
    node.id,
    node.label,
    node.kind,
    node.nodeType,
    item.type,
    item.name,
    item.trigger_mode,
    datasource.id,
    datasource.name,
    datasource.title,
    datasource.description,
    getDagSearchMetadataText(item, node.actionMeta),
  ]);
};

const SEARCH_SOURCE_FIELDS = ['key', 'id', 'label', 'kind', 'nodeType', 'item', 'actionMeta'];
// Keep configuration references private; serializing an index must not expose
// credential values that were deliberately excluded from the searchable text.
const searchIndexSources = new WeakMap();

export const buildDagNodeSearchIndex = (nodes, previousIndex = []) => {
  const previousByKey = new Map(previousIndex.map((entry) => [entry.key, entry]));
  return (nodes || []).map((node) => {
    const previous = previousByKey.get(String(node.key));
    const previousSource = previous ? searchIndexSources.get(previous) : null;
    if (previousSource && SEARCH_SOURCE_FIELDS.every((key) => (
      previousSource[key] === node[key]
      || (key === 'actionMeta' && areDagRecordsEqual(previousSource[key], node[key]))
    ))) return previous;
    const entry = {
      key: String(node.key),
      text: getDagNodeSearchText(node),
    };
    searchIndexSources.set(entry, Object.fromEntries(
      SEARCH_SOURCE_FIELDS.map((key) => [key, node[key]])
    ));
    return entry;
  });
};

export const findDagNodeSearchMatchKeysInIndex = (index, query) => {
  const normalizedQuery = String(query || '').trim().toLowerCase();
  if (!normalizedQuery) return new Set();

  const matches = new Set();
  for (const entry of index || []) {
    if (entry.text.includes(normalizedQuery)) matches.add(entry.key);
  }
  return matches;
};

export const findDagNodeSearchMatchKeys = (nodes, query) => {
  if (!String(query || '').trim()) return new Set();
  return findDagNodeSearchMatchKeysInIndex(buildDagNodeSearchIndex(nodes), query);
};
